import { useEffect, useRef, useState } from "react";
import useGreenieBoard from "../hooks/useGreenieBoard";
import PageLayout from "../components/PageLayout";
import usePilotName from "../hooks/usePilotName";
import { useLens } from "../lens/LensProvider";
import { GreenieBoardFilters, GreeniePilot, GreenieServer, LsoGrade } from "../types/greenieBoard";
import GreenieBoardTable, { TrapTile } from "../components/GreenieBoardTable";
const TRAP_LIMIT = 20;
const MOBILE_TRAP_LIMIT = 6;

function parseGreenieFiltersFromHash(hash: string): GreenieBoardFilters {
  const normalized = (hash || "").replace(/^#/, "");
  const [pathPart, queryString = ""] = normalized.split("?");
  const parts = pathPart.split("/").filter(Boolean);
  if (parts[0] !== "greenie_board") return {};

  const query = new URLSearchParams(queryString);
  const period = query.get("period");

  return {
    unit_family: query.get("unit_family") || undefined,
    server_id: query.get("server_id") || undefined,
    period: period === "this_month" || period === "all_time" ? period : undefined,
  };
}

function isGreenieHash(hash: string): boolean {
  const normalized = (hash || "").replace(/^#/, "");
  const [pathPart] = normalized.split("?");
  const parts = pathPart.split("/").filter(Boolean);
  return parts[0] === "greenie_board";
}

// The page rewrites its own query, so a pilot being viewed through ?pilot= has to be carried along.
function setGreenieHash(filters: GreenieBoardFilters, viewedPilot: string | null): void {
  const query = new URLSearchParams();
  if (viewedPilot) query.set("pilot", viewedPilot);
  if (filters.unit_family) query.set("unit_family", filters.unit_family);
  if (filters.server_id) query.set("server_id", String(filters.server_id));
  if (filters.period && filters.period !== "all_time") query.set("period", filters.period);

  const nextHash = query.toString() ? `#/greenie_board?${query.toString()}` : "#/greenie_board";
  if (window.location.hash !== nextHash) {
    window.history.replaceState(null, "", nextHash);
  }
}

const GRADE_CONFIG: Record<LsoGrade, { label: string; color: string; textColor: string; underline?: boolean; description: string }> = {
  "_OK_": { label: "_OK_", color: "#22c55e", textColor: "#dcfce7", underline: true, description: "Perfect pass" },
  "OK":   { label: "OK",   color: "#166534", textColor: "#bbf7d0",                  description: "Good pass" },
  "(OK)": { label: "(OK)",color: "#166534", textColor: "#bbf7d0",                  description: "Fair pass" },
  "--":   { label: "–",    color: "#334155", textColor: "#94a3b8",                  description: "No grade" },
  "B":    { label: "B",    color: "#64748b", textColor: "#94a3b8",                  description: "Bolter" },
  "WO":   { label: "WO",   color: "#64748b", textColor: "#94a3b8",                  description: "Wave-off" },
  "C":    { label: "C",    color: "#b52929", textColor: "#fecaca",                  description: "Cut — dangerous" },
};

interface FilterDropdownOption { value: string; label: string; }

function FilterDropdown({ placeholder, value, options, onChange }: {
  placeholder: string;
  value: string;
  options: FilterDropdownOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const selected = options.find(o => o.value === value);

  return (
    <div className="gf-dropdown" ref={ref}>
      <button
        type="button"
        className={`facet-input-inner${open ? " is-open" : ""}`}
        onClick={() => setOpen(o => !o)}
      >
        <span>{selected?.label ?? placeholder}</span>
        <svg className="gf-chevron" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="facet-suggestions">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`facet-suggestion${opt.value === value ? " is-active" : ""}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TrapSummary({ pilots }: { pilots: GreeniePilot[] }) {
  const traps = pilots[0]?.traps ?? [];
  if (traps.length === 0) return null;
  const counts = new Map<LsoGrade, number>();
  // A trap with no recorded grade is still a trap: it counts as "no grade" rather than vanishing.
  traps.forEach((trap) => {
    const grade: LsoGrade = trap.grade in GRADE_CONFIG ? trap.grade : "--";
    counts.set(grade, (counts.get(grade) ?? 0) + 1);
  });
  const shown = (Object.keys(GRADE_CONFIG) as LsoGrade[]).filter((grade) => counts.has(grade));
  return (
    <div className="gboard-summary" aria-label="Last traps">
      <span className="gboard-summary-title">Last {traps.length} {traps.length === 1 ? "trap" : "traps"}</span>
      <ul className="gboard-summary-grades">
        {shown.map((grade) => (
          <li key={grade} title={GRADE_CONFIG[grade].description}>
            <span className="gboard-summary-chip" style={{ background: GRADE_CONFIG[grade].color, color: GRADE_CONFIG[grade].textColor }}>
              {GRADE_CONFIG[grade].label}
            </span>
            <span className="gboard-summary-count">{counts.get(grade)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function GreenieBoard() {
  const { activeUid, viewUid, setScope } = useLens();
  const name = usePilotName(activeUid);
  const [filters, setFilters] = useState<GreenieBoardFilters>(() => parseGreenieFiltersFromHash(window.location.hash));
  const { pilots, airframes, servers, loading, error } = useGreenieBoard({ ...filters, pilot_uid: activeUid ?? undefined, pilot_limit: 100 });
  const showEveryone = viewUid
    ? <a href="#/greenie_board">Show everyone</a>
    : <button type="button" onClick={() => setScope("everyone")}>Show everyone</button>;
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 600px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 600px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!isGreenieHash(window.location.hash)) return;
    setGreenieHash(filters, viewUid);
  }, [filters, viewUid]);

  useEffect(() => {
    const onHashChange = () => {
      if (!isGreenieHash(window.location.hash)) return;
      setFilters(parseGreenieFiltersFromHash(window.location.hash));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const legendItems = [
    { key: "_OK_", label: "_OK_", color: "#22c55e", textColor: "#dcfce7", underline: true,  desc: "Perfect pass" },
    { key: "OK",   label: "OK",   color: "#166534", textColor: "#bbf7d0", underline: false, desc: "Good / Fair pass" },
    { key: "--",   label: "–",    color: "#334155", textColor: "#94a3b8", underline: false, desc: "No grade" },
    { key: "B",    label: "B",    color: "#64748b", textColor: "#94a3b8", underline: false, desc: "Bolter" },
    { key: "WO",   label: "WO",   color: "#64748b", textColor: "#94a3b8", underline: false, desc: "Wave-off" },
    { key: "C",    label: "C",    color: "#b52929", textColor: "#fecaca", underline: false, desc: "Cut — dangerous" },
    { key: "night", label: "OK",  color: "#166534", textColor: "#bbf7d0", underline: false, night: true, desc: "Night landing" },
    { key: "wire",  label: "3",   color: "#15803d", textColor: "rgba(255,255,255,0.55)", underline: false, wire: true, desc: "Arresting wire #" },
  ];

  return (
    <PageLayout route="greenie_board" hero={{
      photo: "lso", focus: "50% 45%", title: "Greenie Board",
      body: "Carrier landing grades per pilot, most recent trap on the right.",
    }}>
      <section className="panel panel-tight">
        <div className="gboard-filters">
          <div className="gboard-filters-controls">
            <FilterDropdown
              placeholder="All airframes"
              value={filters.unit_family ?? ""}
              options={[
                { value: "", label: "All airframes" },
                ...airframes.map(a => ({ value: a, label: a }))
              ]}
              onChange={v => setFilters(f => ({ ...f, unit_family: v || undefined }))}
            />
            <FilterDropdown
              placeholder="All servers"
              value={String(filters.server_id ?? "")}
              options={[
                { value: "", label: "All servers" },
                ...(servers as GreenieServer[]).map(s => ({ value: String(s.id), label: s.name }))
              ]}
              onChange={v => setFilters(f => ({ ...f, server_id: v || undefined }))}
            />
            <FilterDropdown
              placeholder="All time"
              value={filters.period ?? "all_time"}
              options={[
                { value: "all_time", label: "All time" },
                { value: "this_month", label: "This month" },
              ]}
              onChange={v => setFilters(f => ({ ...f, period: v as GreenieBoardFilters["period"] }))}
            />
          </div>
        </div>
      </section>

      <section className="panel panel-list">
        {error && <div className="greenie-board-error">{error}</div>}

        {loading && pilots.length === 0 && (
          <div className="status-area"><span className="status">Loading…</span></div>
        )}

        {!loading && !error && pilots.length === 0 && (
          <div className="empty-state">
            {activeUid ? <>{name} has no carrier traps with these filters yet. {showEveryone}</> : "No grading data yet."}
          </div>
        )}

        {activeUid && pilots.length > 0 && <TrapSummary pilots={pilots} />}

        {pilots.length > 0 && (
          <GreenieBoardTable pilots={pilots} trapLimit={isMobile ? MOBILE_TRAP_LIMIT : TRAP_LIMIT} />
        )}

        <div className="gboard-legend">
          {legendItems.map(item => (
            <div key={item.key} className="gboard-legend-item">
              <div className="gboard-legend-tile">
                <TrapTile color={item.color} night={!!item.night} size={22} maskId={`lm-${item.key}`} />
                {item.wire && <span className="gtrap-wire">{item.label}</span>}
                {(item.key === "WO" || item.key === "B") && (
                  <span className="gtrap-label" style={{ color: item.textColor }}>{item.label}</span>
                )}
              </div>
              <span className="gboard-legend-desc">{item.desc}</span>
            </div>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
